import { useMemo } from 'react';

import { RangeSelectorType } from 'modules/api/const';

import { IStakingChartData } from '../../actions/getStakingChartData';
import { getMainDays } from './utils';

export interface IChartPoint {
  date: Date;
  timestamp: number;
  value: number | null;
  isEdge: boolean;
}

const EDGE_PADDING_DAYS = 2;
const TICKS_MONTH = 4;
const TICKS_SIX_MONTHS = 6;

export function useStakingChart(
  data: IStakingChartData,
  range: RangeSelectorType,
) {
  const chartData = useMemo<IChartPoint[]>(() => {
    const today = new Date();
    const end = new Date(
      Date.UTC(today.getFullYear(), today.getMonth(), today.getDate()),
    );
    const mainDays = getMainDays(range);

    const start = new Date(end);
    start.setUTCDate(end.getUTCDate() - (mainDays - 1));

    const outerStart = new Date(start);
    outerStart.setUTCDate(start.getUTCDate() - EDGE_PADDING_DAYS);

    const outerEnd = new Date(end);
    outerEnd.setUTCDate(end.getUTCDate());

    const dateList: Date[] = [];
    for (
      let d = new Date(outerStart);
      d <= outerEnd;
      d.setUTCDate(d.getUTCDate() + 1)
    ) {
      dateList.push(new Date(d));
    }

    const sortedData = data
      .map(({ timestamp, tvl }) => {
        return {
          dateStr: new Date(timestamp * 1000).toISOString().slice(0, 10),
          value: parseFloat(tvl.toString()),
        };
      })
      .sort((a, b) => a.dateStr.localeCompare(b.dateStr));

    const dataMap = new Map(sortedData.map(d => [d.dateStr, d.value]));
    const firstValue = sortedData[0]?.value ?? null;
    const lastValue = sortedData[sortedData.length - 1]?.value ?? null;

    return dateList.map((date, i, arr) => {
      const iso = date.toISOString().slice(0, 10);
      let value: number | null = null;

      if (dataMap.has(iso)) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        value = dataMap.get(iso)!;
      } else if (i === 0) {
        value = firstValue;
      } else if (i === arr.length - 1) {
        value = lastValue;
      }

      return {
        date,
        timestamp: date.getTime(),
        value,
        isEdge: i === 0 || i === arr.length - 1,
      };
    });
  }, [data, range]);

  const monthTickTimestamps = useMemo(() => {
    const central = chartData.filter(d => !d.isEdge);
    const step = Math.floor(central.length / TICKS_MONTH);
    const offset = Math.floor((central.length - step * (TICKS_MONTH - 1)) / 2);
    return Array.from(
      { length: TICKS_MONTH },
      (_, i) => central[offset + i * step]?.timestamp,
    ).filter(Boolean);
  }, [chartData]);

  const sixMonthsTickTimestamps = useMemo(() => {
    const central = chartData.filter(d => !d.isEdge);
    const step = Math.floor(central.length / TICKS_SIX_MONTHS);
    const offset = Math.floor(
      (central.length - step * (TICKS_SIX_MONTHS - 1)) / 2,
    );
    return Array.from(
      { length: TICKS_SIX_MONTHS },
      (_, i) => central[offset + i * step]?.timestamp,
    ).filter(Boolean);
  }, [chartData]);

  return {
    chartData,
    monthTickTimestamps,
    sixMonthsTickTimestamps,
  };
}
